import React from 'react';
import { Helmet } from 'react-helmet-async';
import C2CHero from '../../components/campus2container/C2CHero';
import C2CAbout from '../../components/campus2container/C2CAbout';
import C2CCurriculum from '../../components/campus2container/C2CCurriculum';
import C2CFreePackage from '../../components/campus2container/C2CFreePackage';
import C2CUpgrade from '../../components/campus2container/C2CUpgrade';
import C2CSuccessPathway from '../../components/campus2container/C2CSuccessPathway';
import C2CEligibility from '../../components/campus2container/C2CEligibility';
import C2CHowToJoin from '../../components/campus2container/C2CHowToJoin';
import C2CFAQ from '../../components/campus2container/C2CFAQ';
import C2CFinalCTA from '../../components/campus2container/C2CFinalCTA';

const Campus2Container = () => {
  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Campus2Container - 6-Week FREE Export Training for Students & NYSC | LSEM Nigeria</title>
        <meta 
          name="description" 
          content="Join Campus2Container, a revolutionary 6-week FREE export training program for final-year students and NYSC members. Learn export documentation, digital tools, and global market strategies. Transform from graduate to global entrepreneur with LSEM Nigeria - completely FREE!" 
        />
        <meta 
          name="keywords" 
          content="Campus2Container, free export training students, NYSC export program free, final year students export, Nigeria student export training, youth export program, global entrepreneur training, export mentorship students, LSEM student program, Lagos export training, free export course" 
        />
        <meta name="author" content="Lagos School of Export Management" />
        <meta name="robots" content="index, follow" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://lsem.org.ng/campus2container" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lsem.org.ng/campus2container" />
        <meta property="og:title" content="Campus2Container - 6-Week FREE Export Training for Students & NYSC | LSEM" />
        <meta 
          property="og:description" 
          content="Revolutionary 6-week FREE export training program for final-year students and NYSC members. Learn practical export skills, documentation, and digital tools. Join 500+ successful student exporters - 100% FREE!" 
        />
        <meta 
          property="og:image" 
          content="https://lsem.org.ng/images/campus2container-og-image.jpg" 
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Campus2Container - Export Training for Students" />
        <meta property="og:site_name" content="Lagos School of Export Management" />
        <meta property="og:locale" content="en_NG" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@LSEMNigeria" />
        <meta name="twitter:creator" content="@LSEMNigeria" />
        <meta name="twitter:url" content="https://lsem.org.ng/campus2container" />
        <meta name="twitter:title" content="Campus2Container - 6-Week FREE Export Training for Students & NYSC" />
        <meta 
          name="twitter:description" 
          content="Revolutionary FREE export training program for students. 6 weeks to transform from graduate to global entrepreneur. Join 500+ successful student exporters - completely FREE!" 
        />
        <meta 
          name="twitter:image" 
          content="https://lsem.org.ng/images/campus2container-twitter-image.jpg" 
        />
        <meta name="twitter:image:alt" content="Campus2Container Export Training Program" />
        
        {/* LinkedIn */}
        <meta property="linkedin:card" content="summary_large_image" />
        <meta property="linkedin:title" content="Campus2Container - FREE Export Training for Students & NYSC Corps Members" />
        <meta 
          property="linkedin:description" 
          content="6-week intensive FREE export training program designed for final-year students and NYSC members. Learn export documentation, digital marketing, and global trade strategies - completely FREE!" 
        />
        <meta 
          property="linkedin:image" 
          content="https://lsem.org.ng/images/campus2container-linkedin-image.jpg" 
        />
        
        {/* WhatsApp */}
        <meta property="whatsapp:title" content="Campus2Container - FREE Export Training for Students" />
        <meta 
          property="whatsapp:description" 
          content="Join the revolutionary 6-week FREE export training program for students. Transform from graduate to global entrepreneur with LSEM Nigeria - 100% FREE!" 
        />
        <meta 
          property="whatsapp:image" 
          content="https://lsem.org.ng/images/campus2container-whatsapp-image.jpg" 
        />
        
        {/* Additional SEO Meta Tags */}
        <meta name="geo.region" content="NG-LA" />
        <meta name="geo.placename" content="Lagos, Nigeria" />
        <meta name="geo.position" content="6.5244;3.3792" />
        <meta name="ICBM" content="6.5244, 3.3792" />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            "name": "Campus2Container - FREE Export Training Program",
            "description": "6-week FREE export training program for final-year students and NYSC members to learn international trade and export skills at no cost",
            "provider": {
              "@type": "Organization",
              "name": "Lagos School of Export Management",
              "url": "https://lsem.org.ng",
              "logo": "https://lsem.org.ng/logo1.png",
              "address": {
                "@type": "PostalAddress",
                "addressCountry": "Nigeria",
                "addressRegion": "Lagos State",
                "addressLocality": "Lagos"
              }
            },
            "courseMode": "blended",
            "duration": "P6W",
            "inLanguage": "en",
            "audience": {
              "@type": "EducationalAudience",
              "educationalRole": "student"
            },
            "teaches": [
              "Export Documentation",
              "International Trade",
              "Digital Marketing for Export",
              "Global Market Strategies",
              "Export Business Development"
            ],
            "url": "https://lsem.org.ng/campus2container",
            "image": "https://lsem.org.ng/images/campus2container-og-image.jpg",
            "offers": {
              "@type": "Offer",
              "category": "Educational",
              "availability": "https://schema.org/InStock",
              "price": "0",
              "priceCurrency": "NGN",
              "priceValidUntil": "2025-12-31",
              "description": "Free 6-week export training program for students"
            }
          })}
        </script>
        
        {/* Educational Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EducationalOrganization",
            "name": "Lagos School of Export Management",
            "alternateName": "LSEM Nigeria",
            "url": "https://lsem.org.ng",
            "logo": "https://lsem.org.ng/logo1.png",
            "description": "Leading export training institution in Nigeria specializing in international trade education and certification",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "Nigeria",
              "addressRegion": "Lagos State",
              "addressLocality": "Lagos"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "url": "https://lsem.org.ng/contact"
            },
            "sameAs": [
              "https://www.facebook.com/LSEMNigeria",
              "https://www.twitter.com/LSEMNigeria",
              "https://www.instagram.com/LSEMNigeria",
              "https://www.linkedin.com/company/lsem-nigeria"
            ]
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-white">
        <C2CHero />
        <C2CAbout />
        <C2CCurriculum />
        <C2CFreePackage />
        <C2CUpgrade />
        <C2CSuccessPathway />
        <C2CEligibility />
        <C2CHowToJoin />
        <C2CFAQ />
        <C2CFinalCTA />
      </div>
    </>
  );
};

export default Campus2Container;